import { useEffect, useState } from 'react';
import { resolveSignatureUrl } from 'api/signatures';

/**
 * Hook para resolver un mapa de firmas (S3 keys o paths legacy) a URLs
 * renderizables, ANTES de montar/generar un PDF con `@react-pdf/renderer`.
 *
 * @param {Record<string, string|undefined|null>} values
 *   Objeto `{ alias: key }`. Ej.: `{ spec: voucher.specialist_signature }`.
 *   La identidad del objeto se ignora: el hook compara valores por entrada,
 *   así que no requiere `useMemo` del lado del caller.
 * @returns {{ urls: Record<string, string|null>, loading: boolean, ready: boolean }}
 *   - `urls`: mismo shape que `values` pero con URLs ya resueltas.
 *   - `loading`: true mientras se está resolviendo.
 *   - `ready`: true cuando `urls` refleja el último `values` recibido.
 */
export function useSignatureUrls(values) {
  const entries = Object.entries(values || {});
  const signature = entries.map(([k, v]) => `${k}=${v ?? ''}`).join('|');

  const [state, setState] = useState({ urls: {}, loading: true, ready: false });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, ready: false }));
    Promise.all(entries.map(([, v]) => resolveSignatureUrl(v)))
      .then((resolved) => {
        if (cancelled) return;
        const urls = {};
        entries.forEach(([k], i) => {
          urls[k] = resolved[i];
        });
        setState({ urls, loading: false, ready: true });
      })
      .catch((err) => {
        if (cancelled) return;

        setState({ urls: {}, loading: false, ready: true });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return state;
}

export default useSignatureUrls;
